// Enums matching backend schemas

export enum DistributionStatus {
  DRAFT = 'draft',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

// Raw wire-format types (backend Decimal fields arrive as JSON strings)

export interface RawFundMetricsImpact {
  total_distributed_before: string;
  total_distributed_after: string;
  dpi_before: string;
  dpi_after: string;
  nav_before: string;
  nav_after: string;
  gp_carry_before: string;
  gp_carry_after: string;
}

export interface RawFundWaterfallTerms {
  waterfall_type: string;
  hurdle_rate: string;
  carried_interest: string;
  gp_catch_up: string;
}

export interface RawAssetSale {
  id: string;
  portfolio_company_name: string | null;
  transaction_name: string | null;
  transaction_date: string;
  sale_amount: string | null;
}

export interface RawBackendInvestorAllocation {
  investor_fund_relationship_id: string;
  investor_name: string | null;
  commitment_amount: string;
  commitment_percentage: string;
  amount: string;
  return_of_capital: string;
  preferred_return: string;
  catch_up: string;
  carried_interest_lp: string;
  carried_interest_gp: string;
}

export interface RawDistribution {
  id: string;
  fund_id: string;
  fund_name: string | null;
  distribution_number: number;
  status: DistributionStatus;
  distribution_date: string;
  currency: string;
  amount: string;
  portfolio_company_id: string | null;
  portfolio_company_name: string | null;
  asset_sales: RawAssetSale[];
  description: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  payment_due_date?: string | null;
  exit_value?: string;
  total_expenses?: string;
  shares_sold?: string | null;
  price_per_share?: string | null;
  cost_basis?: string | null;
  cost_per_share?: string | null;
  waterfall_calculated?: boolean;
  waterfall_tiers?: RawWaterfallTier[] | null;
  investor_allocations?: RawBackendInvestorAllocation[];
  total_investors?: number;
  investors_paid?: number;
  bank_transaction_id?: string | null;
  bank_transaction_matched?: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  fund_metrics_impact?: RawFundMetricsImpact | null;
}

export interface RawDistributionSummary {
  id: string;
  fund_id: string;
  fund_name: string | null;
  distribution_number: number;
  status: DistributionStatus;
  distribution_date: string;
  amount: string;
  currency: string;
  description: string | null;
  created_at: string;
  fund_transaction_status: string | null;
  exit_value?: string;
  total_investors?: number;
  investors_paid?: number;
  bank_transaction_matched?: boolean;
  fund_waterfall_terms?: RawFundWaterfallTerms | null;
}

export interface RawAvailableAssetSale {
  id: string;
  transaction_name: string | null;
  portfolio_company_id: string;
  portfolio_company_name: string | null;
  transaction_date: string;
  sale_amount: string | null;
  transaction_status: string;
}

export interface RawWaterfallTier {
  name: string;
  amount: string;
  percentage: string;
}

export interface RawWizardInvestorAllocation {
  investor_id: string;
  investor_fund_relationship_id: string;
  investor_name: string;
  commitment_amount: string;
  commitment_percentage: string;
  return_of_capital: string;
  preferred_return: string;
  catch_up: string;
  carried_interest_lp: string;
  carried_interest_gp: string;
  total_distribution: string;
  has_custom_terms?: boolean;
  custom_hurdle_rate?: string | null;
  custom_carried_interest?: string | null;
  custom_catch_up_rate?: string | null;
  bank_account_verified: boolean;
  payment_status: 'pending' | 'processing' | 'paid' | 'failed';
}

export interface RawWaterfallCalculationResponse {
  waterfall_tiers: RawWaterfallTier[];
  investor_allocations: RawWizardInvestorAllocation[];
  fund_terms: {
    hurdle_rate: string;
    carried_interest: string;
    catch_up_rate: string;
    waterfall_type: string;
  };
  total_lp_distribution: string;
  total_gp_distribution: string;
}

export interface RawDistributionListResponse {
  items: RawDistributionSummary[];
  total_count: number;
  page: number;
  page_size: number;
}

export type DistributionAction =
  | 'view_details'
  | 'generate_notices'
  | 'send_notices'
  | 'download_notices'
  | 'cancel';

export interface FundMetricsImpact {
  total_distributed_before: number;
  total_distributed_after: number;
  dpi_before: number;
  dpi_after: number;
  nav_before: number;
  nav_after: number;
  gp_carry_before: number;
  gp_carry_after: number;
}

export interface DistributionNotice {
  id: string;
  investor_id: string;
  investor_name: string;
  distribution_id: string;
  status: 'pending' | 'generated' | 'sent' | 'failed';
  generated_at: string | null;
  sent_at: string | null;
  email: string | null;
  pdf_url: string | null;
}

// Interfaces

export interface FundWaterfallTerms {
  waterfall_type: string;
  hurdle_rate: number;
  carried_interest: number;
  gp_catch_up: number;
}

export interface DistributionSummary {
  id: string;
  fund_id: string;
  fund_name: string | null;
  distribution_number: number;
  status: DistributionStatus;
  distribution_date: string;
  amount: number;
  currency: string;
  description: string | null;
  created_at: string;
  fund_transaction_status: string | null;

  // Optional fields (not always returned from backend)
  exit_value?: number;
  total_investors?: number;
  investors_paid?: number;
  bank_transaction_matched?: boolean;
  fund_waterfall_terms?: FundWaterfallTerms | null;
}

export interface AssetSale {
  id: string;
  portfolio_company_name: string | null;
  transaction_name: string | null;
  transaction_date: string;
  sale_amount: number | null;
}

export interface Distribution {
  id: string;
  fund_id: string;
  fund_name: string | null;
  distribution_number: number;
  status: DistributionStatus;
  distribution_date: string;
  currency: string;
  amount: number;
  portfolio_company_id: string | null;
  portfolio_company_name: string | null;
  asset_sales: AssetSale[];
  description: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;

  // Optional fields (not always returned from backend)
  payment_due_date?: string | null;
  exit_value?: number;
  total_expenses?: number;
  shares_sold?: number | null;
  price_per_share?: number | null;
  cost_basis?: number | null;
  cost_per_share?: number | null;
  waterfall_calculated?: boolean;
  waterfall_tiers?: WaterfallTier[] | null;
  investor_allocations?: BackendInvestorAllocation[];
  total_investors?: number;
  investors_paid?: number;
  bank_transaction_id?: string | null;
  bank_transaction_matched?: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  fund_metrics_impact?: FundMetricsImpact | null;
}

export interface AvailableAssetSale {
  id: string;
  transaction_name: string | null;
  portfolio_company_id: string;
  portfolio_company_name: string | null;
  transaction_date: string;
  sale_amount: number | null;
  transaction_status: string;
}

export interface DistributionCreateResponse {
  id: string;
}

export interface DistributionFinalizeResponse {
  distribution_id: string;
  status: string;
  pdfs_generated: number;
  pdfs_failed: number;
  emails_queued: number;
  emails_failed: number;
}

export interface WaterfallTier {
  name: string;
  description: string;
  amount: number;
  percentage: number;
}

export interface InvestorAllocation {
  investor_fund_relationship_id: string;
  investor_name: string;
  commitment_amount: number;
  commitment_percentage: number;
  return_of_capital: number;
  preferred_return: number;
  catch_up: number;
  carried_interest_lp: number;
  carried_interest_gp: number;
  total_distribution: number;
  has_custom_terms: boolean;
  custom_hurdle_rate?: number | null;
  custom_carried_interest?: number | null;
  custom_catch_up_rate?: number | null;
  bank_account_verified: boolean;
  payment_status: string;
  payment_date: string | null;
  payment_reference: string | null;
}

export interface DistributionListResponse {
  items: DistributionSummary[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface WaterfallCalculationResponse {
  waterfall_tiers: WaterfallTier[];
  investor_allocations: WizardInvestorAllocation[];
  fund_terms: {
    hurdle_rate: number;
    carried_interest: number;
    catch_up_rate: number;
    waterfall_type: string;
  };
  total_lp_distribution: number;
  total_gp_distribution: number;
}

export interface DistributionWizardData {
  fund_id: string;
  transaction_name?: string;
  portfolio_company_id?: string;
  amount: number;
  investor_allocations?: WizardInvestorAllocation[];
  waterfall_tiers?: WaterfallTier[];
  total_lp_distribution?: number;
  total_gp_distribution?: number;
  distribution_date: string;
  notes?: string;
  asset_sale_transaction_ids?: string[];
}

export interface WizardInvestorAllocation {
  investor_id: string;
  investor_fund_relationship_id: string;
  investor_name: string;
  commitment_amount: number;
  commitment_percentage: number;
  return_of_capital: number;
  preferred_return: number;
  catch_up: number;
  carried_interest_lp: number;
  carried_interest_gp: number;
  total_distribution: number;
  has_custom_terms?: boolean;
  custom_hurdle_rate?: number | null;
  custom_carried_interest?: number | null;
  custom_catch_up_rate?: number | null;
  bank_account_verified: boolean;
  payment_status: 'pending' | 'processing' | 'paid' | 'failed';
}

export interface BackendInvestorAllocation {
  investor_fund_relationship_id: string;
  investor_name: string | null;
  commitment_amount: number;
  commitment_percentage: number;
  amount: number;
  return_of_capital: number;
  preferred_return: number;
  catch_up: number;
  carried_interest_lp: number;
  carried_interest_gp: number;
}

interface InvestorAllocationCreateItem {
  investor_fund_relationship_id: string;
  amount: number;
  return_of_capital: number;
  preferred_return: number;
  catch_up: number;
  carried_interest_lp: number;
  carried_interest_gp: number;
}

export interface DistributionCreateRequest {
  distribution_date: string;
  amount: number;
  exit_value?: number;
  total_expenses?: number;
  portfolio_company_id?: string;
  shares_sold?: number;
  price_per_share?: number;
  description?: string;
  notes?: string;
  asset_sale_transaction_ids?: string[];
  investor_allocations?: InvestorAllocationCreateItem[];
}

export interface DistributionUpdateRequest {
  status?: DistributionStatus;
  distribution_date?: string;
  payment_due_date?: string;
  exit_value?: number;
  total_expenses?: number;
  amount?: number;
  description?: string;
  notes?: string;
}
